import { z } from "zod";
import { msg } from "@lingui/core/macro";
import { getI18n } from "~/lib/i18n/i18n";
import { getMinPasswordLength } from "~/lib/security/password-config";
import { validatePassword } from "~/lib/security/utils/password-validator";

/**
 * Login form validation schema
 */
export const loginFormSchema = z.object({
  email: z.email({ error: () => getI18n()._(msg`Please enter a valid email`) }).min(1, {
    error: () => getI18n()._(msg`Email is required`),
  }),
  password: z.string().min(1, { error: () => getI18n()._(msg`Password is required`) }),
});


/**
 * Forgot password form validation schema
 */
export const forgotPasswordFormSchema = z.object({
  email: z.email({ error: () => getI18n()._(msg`Please enter a valid email`) }).min(1, {
    error: () => getI18n()._(msg`Email is required`),
  }),
});


/**
 * Reset password form validation schema
 */
export const resetPasswordFormSchema = z.object({
  newPassword: z
    .string()
    .min(1, { error: () => getI18n()._(msg`Password is required`) })
    .min(8, { error: () => getI18n()._(msg`Password must be at least 8 characters`) }),
  confirmPassword: z
    .string()
    .min(1, { error: () => getI18n()._(msg`Please confirm your password`) }),
});


/**
 * Base signup form field schemas
 */
const signupFormFieldSchemas = {
  name: z
    .string()
    .min(1, { error: () => getI18n()._(msg`Name is required`) })
    .min(2, { error: () => getI18n()._(msg`Name must be at least 2 characters`) }),
  email: z.email({ error: () => getI18n()._(msg`Please enter a valid email`) }).min(1, {
    error: () => getI18n()._(msg`Email is required`),
  }),
  password: z
    .string()
    .min(1, { error: () => getI18n()._(msg`Password is required`) })
    .check((ctx) => {
      const minLen = getMinPasswordLength();
      if (ctx.value.length < minLen) {
        ctx.issues.push({
          code: "custom",
          message: getI18n()._(msg`Password must be at least ${minLen} characters`),
          input: ctx.value,
          path: [],
        });
      }
    }),
  confirmPassword: z
    .string()
    .min(1, { error: () => getI18n()._(msg`Please confirm your password`) }),
};

/**
 * Signup form field validators for TanStack Form
 */
export const signupFormFields = {
  name: ({ value }: { value: string }) => {
    try {
      signupFormFieldSchemas.name.parse(value);
      return undefined;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.issues[0]?.message || getI18n()._(msg`Invalid name`);
      }
      return getI18n()._(msg`Invalid name`);
    }
  },
  email: ({ value }: { value: string }) => {
    try {
      signupFormFieldSchemas.email.parse(value);
      return undefined;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.issues[0]?.message || getI18n()._(msg`Invalid email`);
      }
      return getI18n()._(msg`Invalid email`);
    }
  },
  password: ({ value }: { value: string }) => {
    try {
      signupFormFieldSchemas.password.parse(value);
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.issues[0]?.message || getI18n()._(msg`Invalid password`);
      }
      return getI18n()._(msg`Invalid password`);
    }

    const validation = validatePassword(value);
    if (!validation.isValid) {
      return (
        validation.errors[0] ?? getI18n()._(msg`Password does not meet requirements`)
      );
    }

    return undefined;
  },
  confirmPassword: ({ value }: { value: string }) => {
    try {
      signupFormFieldSchemas.confirmPassword.parse(value);
      return undefined;
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.issues[0]?.message || getI18n()._(msg`Please confirm your password`);
      }
      return getI18n()._(msg`Please confirm your password`);
    }
  },
};

/**
 * Signup form validation schema
 */
export const signupFormSchema = z
  .object(signupFormFieldSchemas)
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    error: () => getI18n()._(msg`Passwords do not match`),
  });

